class HowItWorksTabs extends HTMLElement {
  static panelSelector = '.section-how-it-works--tab-panel[data-how-it-works-key]';
  static initScheduled = false;
  static eventsBound = false;

  connectedCallback() {
    HowItWorksTabs.queueInit();
  }

  static queueInit() {
    if (HowItWorksTabs.initScheduled) return;

    HowItWorksTabs.initScheduled = true;

    requestAnimationFrame(() => {
      HowItWorksTabs.initScheduled = false;
      HowItWorksTabs.bindEvents();
      HowItWorksTabs.applyInitialTab();
    });
  }

  static bindEvents() {
    if (HowItWorksTabs.eventsBound) return;

    HowItWorksTabs.eventsBound = true;
    document.addEventListener('click', HowItWorksTabs.onClick);
    document.addEventListener('keydown', HowItWorksTabs.onKeydown);
  }

  static onClick(event) {
    const button = event.target.closest('how-it-works-tabs [role="tab"]');
    if (!button) return;

    const { tabKey } = button.dataset;
    if (!tabKey) return;

    event.preventDefault();
    HowItWorksTabs.selectTabKey(tabKey, { focusButton: button });
  }

  static onKeydown(event) {
    const button = event.target.closest('how-it-works-tabs [role="tab"]');
    if (!button) return;

    const tablist = button.closest('[role="tablist"]');
    if (!tablist) return;

    const buttons = [...tablist.querySelectorAll('[role="tab"]')];
    const index = buttons.indexOf(button);
    if (index === -1) return;

    let nextIndex = null;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = index > 0 ? index - 1 : buttons.length - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = index < buttons.length - 1 ? index + 1 : 0;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = buttons.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    HowItWorksTabs.selectTabKey(buttons[nextIndex].dataset.tabKey, { focusButton: buttons[nextIndex] });
  }

  static getAllTabButtons() {
    return [...document.querySelectorAll('how-it-works-tabs [role="tab"]')];
  }

  static getTabKeys() {
    return [...new Set(
      HowItWorksTabs.getAllTabButtons()
        .map((button) => button.dataset.tabKey)
        .filter(Boolean),
    )];
  }

  static slugify(key) {
    return key
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  static getPanelByKey(tabKey) {
    if (!tabKey) return null;

    const panelById = document.getElementById(`how-it-works-panel-${HowItWorksTabs.slugify(tabKey)}`);
    if (panelById) return panelById;

    return [...document.querySelectorAll(HowItWorksTabs.panelSelector)]
      .find((panel) => panel.dataset.howItWorksKey === tabKey) ?? null;
  }

  static pauseVideosInPanel(panel) {
    if (!panel) return;

    panel.querySelectorAll('.section-how-it-works__video').forEach((videoContainer) => {
      videoContainer.classList.remove('active');

      const video = videoContainer.querySelector('video');
      if (video) {
        video.pause();
      }
    });
  }

  static setSectionVisible(panel, isVisible) {
    const root = panel.closest('.shopify-section') ?? panel;

    panel.classList.toggle('section-how-it-works--tab-active', isVisible);
    panel.style.removeProperty('display');

    root.classList.toggle('section-how-it-works-tabs-section--visible', isVisible);
    root.toggleAttribute('hidden', !isVisible);

    if (isVisible) {
      root.style.removeProperty('display');
    } else {
      HowItWorksTabs.pauseVideosInPanel(panel);
    }
  }

  static updateButtonStates(activeTabKey) {
    HowItWorksTabs.getAllTabButtons().forEach((button) => {
      const isActive = button.dataset.tabKey === activeTabKey;

      button.classList.toggle('section-how-it-works-tabs__button--active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  static applyInitialTab() {
    const buttons = HowItWorksTabs.getAllTabButtons();
    if (!buttons.length) return;

    const defaultButton = buttons.find((button) => button.getAttribute('aria-selected') === 'true') ?? buttons[0];
    HowItWorksTabs.selectTabKey(defaultButton.dataset.tabKey, { silent: true });
  }

  static selectTabKey(tabKey, { focusButton = null, silent = false } = {}) {
    if (!tabKey) return;

    const panel = HowItWorksTabs.getPanelByKey(tabKey);
    if (!panel) {
      console.warn(`[how-it-works-tabs] Panel not found for tab key: ${tabKey}`);
      return;
    }

    HowItWorksTabs.updateButtonStates(tabKey);

    HowItWorksTabs.getTabKeys().forEach((key) => {
      const tabPanel = HowItWorksTabs.getPanelByKey(key);
      if (tabPanel) {
        HowItWorksTabs.setSectionVisible(tabPanel, key === tabKey);
      }
    });

    if (!silent) {
      (focusButton ?? HowItWorksTabs.getAllTabButtons().find((button) => button.dataset.tabKey === tabKey))?.focus();
    }
  }
}

if (!customElements.get('how-it-works-tabs')) {
  customElements.define('how-it-works-tabs', HowItWorksTabs);
}